import { Injectable, Injector } from '@angular/core';
import {
    HttpRequest,
    HttpErrorResponse,
    HttpHandler,
    HttpEvent,
    HttpInterceptor
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { switchMap, catchError, finalize, filter, take } from 'rxjs/operators';

import { AuthenticationService } from './authentication.service';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {

    isRefreshingToken = false;
    tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(private authService: AuthenticationService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.authService.isAccessTokenExpired && !this.isRefreshingToken) {
            return this.handle401Error(request, next);
        }

        return next.handle(this.addTokenToRequest(request, this.authService.accessToken))   // authService.accessToken
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 401) {
                        return this.handle401Error(request, next);
                    } else {
                        return throwError(error);
                    }
                }));
    }


    private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
        if (!request.headers.has('Content-Type') && !(request.body instanceof FormData)) {
            request = request.clone({ headers: request.headers.set('Content-Type', 'application/json') });
        }

        request = request.clone({ headers: request.headers.set('Accept', 'application/json') });

        if (token) {
            request = request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
        }

        return request;
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.tokenSubject.next(null);

            return this.authService.refreshToken()
                .pipe(
                    switchMap((user: any) => {
                        this.tokenSubject.next(user.accessToken);
                        return next.handle(this.addTokenToRequest(request, this.authService.accessToken));
                    }),
                    catchError((error) => {
                        this.authService.logout();
                        return of(null);
                    }),
                    finalize(() => {
                        this.isRefreshingToken = false;
                    })
                );
        } else {
            return this.tokenSubject
                .pipe(filter(token => token != null),
                    take(1),
                    switchMap(token => {
                        return next.handle(this.addTokenToRequest(request, token));
                    }));
        }
    }
}
